<template>
    <div class="ds-employee-form-section">
        <div class="ds-employee-form-section-divider mb-4">
            <div class="ds-employee-form-section-title">Настройки доступа</div>
        </div>

        <EmployeeDataItem label="Логин" :value="user.phone" />

        <template v-if="isPasswordFormOpen">
            <div class="ds-employee-form-data-grid mt-5">
                <base-input v-model="email" :disabled="isSaving" max-length="50">E-mail</base-input>
                <div class="ds-employee-password-control">
                    <base-input
                        class="flex-1"
                        :model-value="isPasswordCopied ? 'Пароль скопирован' : password"
                        :disabled="isSaving"
                        :readonly="isPasswordCopied"
                        max-length="64"
                        @update:model-value="updatePassword"
                    >Пароль
                        <template #right>
                            <button
                                type="button"
                                class="ds-employee-password-copy"
                                :disabled="isSaving"
                                aria-label="Скопировать пароль"
                                title="Скопировать пароль"
                                @click="copyPassword"
                            ><icon width="16" height="16" vb-size="24"><IconCopy /></icon></button>
                        </template>
                    </base-input>
                    <button
                        type="button"
                        class="ds-employee-password-refresh"
                        :disabled="isSaving"
                        aria-label="Сгенерировать пароль заново"
                        title="Сгенерировать пароль заново"
                        @click="generatePassword"
                    ><img class="ds-employee-password-refresh-icon" :src="refreshIcon" width="16" height="16" alt=""></button>
                </div>
            </div>
            <checkbox
                class="text-sm mt-4"
                :model-value="sendToEmail"
                :disabled="isSaving || !isEmailValid"
                @update:model-value="sendToEmail = $event"
            >Отправить на email</checkbox>
            <div class="pt-4 text-right">
                <btn :disabled="isSaving" :class="{ 'opacity-50': isSaving }" @click="save">Сохранить</btn>
            </div>
        </template>

        <div v-else class="pt-4 text-right">
            <btn @click="openPasswordForm">Восстановить пароль</btn>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import EmployeeDataItem from './EmployeeDataItem.vue'
import { resetUserPassword } from '@/api/users'
import refreshIcon from '@/assets/icons/IconRefreshCcw.svg'
import IconCopy from '@/ui/icons/IconCopy.vue'

interface Props {
    user: {
        id: string
        phone: string
        email?: string
    }
}

const { user } = defineProps<Props>()
const toast = useToast({ position: 'top' })

const isPasswordFormOpen = ref(false)
const isSaving = ref(false)
const email = ref('')
const password = ref('')
const sendToEmail = ref(false)
const isPasswordCopied = ref(false)
let passwordCopyTimeout: ReturnType<typeof setTimeout> | undefined

const resetPasswordCopied = () => {
    isPasswordCopied.value = false
    if (passwordCopyTimeout) clearTimeout(passwordCopyTimeout)
    passwordCopyTimeout = undefined
}

const updatePassword = (value: string | number) => {
    password.value = String(value)
}

const isEmailValid = computed(() => /^[A-Z0-9._%+-]+@[A-Z0-9-]+\.[A-Z]{2,}$/i.test(email.value))

watch(isEmailValid, (valid) => {
    if (!valid) sendToEmail.value = false
})

const generatePassword = () => {
    resetPasswordCopied()
    const lowerCase = 'abcdefghijklmnopqrstuvwxyz'
    const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const digits = '0123456789'
    const allCharacters = lowerCase + upperCase + digits
    const randomIndex = (length: number) => crypto.getRandomValues(new Uint32Array(1))[0] % length
    const characters = [
        lowerCase[randomIndex(lowerCase.length)],
        upperCase[randomIndex(upperCase.length)],
        digits[randomIndex(digits.length)]
    ]

    while (characters.length < 12) {
        characters.push(allCharacters[randomIndex(allCharacters.length)])
    }

    password.value = characters.sort(() => crypto.getRandomValues(new Uint32Array(1))[0] - 0x80000000).join('')
}

const copyPassword = async () => {
    if (!password.value) return

    if (!navigator.clipboard) {
        console.error('Не удалось скопировать пароль: буфер обмена недоступен')
        return
    }

    try {
        await navigator.clipboard.writeText(password.value)
        isPasswordCopied.value = true
        if (passwordCopyTimeout) clearTimeout(passwordCopyTimeout)
        passwordCopyTimeout = setTimeout(() => {
            isPasswordCopied.value = false
        }, 3000)
    } catch (error) {
        console.error('Не удалось скопировать пароль', error)
    }
}

const openPasswordForm = () => {
    email.value = user.email || ''
    sendToEmail.value = false
    generatePassword()
    isPasswordFormOpen.value = true
}

onBeforeUnmount(() => {
    resetPasswordCopied()
})

const save = async () => {
    if (isSaving.value) return

    isSaving.value = true
    const data = {
        password: password.value,
        send_to_email: sendToEmail.value,
        ...(sendToEmail.value ? { email: email.value } : {})
    }

    try {
        const response = await resetUserPassword(user.id, data)
        if (response.data.status === 'error') {
            toast.error(response.data.msg || 'Не удалось восстановить пароль')
            return
        }

        toast.success('Пароль восстановлен')
        resetPasswordCopied()
        isPasswordFormOpen.value = false
    } catch (error: unknown) {
        const message = (error as { response?: { data?: { msg?: string } } }).response?.data?.msg
        toast.error(message || 'Не удалось восстановить пароль')
    } finally {
        isSaving.value = false
    }
}
</script>
